'use client';
// Shared "elegant typewriter" reveal — smooth ease-out character reveal, extracted from
// WaSmartButton.tsx (the original on-page WA bubble) so every WA-message preview site-wide
// (WaSmartButton's own bubble, CompactWaBar's pinned bottom bar, CompactIntake's inline
// layout preview) types out new text the same way instead of snapping it in instantly.
import { useState, useEffect, useRef } from 'react';

export function useTypewriter(target: string, skip = false): { typed: string; isTyping: boolean } {
  const [typed, setTyped] = useState(target);
  const prev = useRef<string>(target);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (skip) return; // e.g. never animate while the user is editing
    if (prev.current === target) return;
    prev.current = target;
    if (raf.current) cancelAnimationFrame(raf.current);
    const len = target.length;
    const duration = Math.min(850, 280 + len * 8);
    let start = 0;
    const tick = (ts: number) => {
      if (!start) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const n = Math.floor(eased * len);
      setTyped(target.slice(0, n));
      if (t < 1) raf.current = requestAnimationFrame(tick);
      else setTyped(target);
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, skip]);

  return { typed, isTyping: !skip && typed !== target };
}
